import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IconButton, icons, Loader } from '@ublend-npm/aulaui-next';
import { Lti1P3Provider } from '@ublend-npm/aula-schema';
import { LTI_1P3_START, API_URL } from '@core/constants/endpoints';
import { currentUser } from '@core/selectors/user';
import { getCurrentSpaceId } from '@core/selectors/space';
import uuid from '@core/utils/uuid';
import saveLti1p3Assignment from '@core/api/lti/saveLti1p3Assignment';
import fetchDeepLinkReturn from '@core/api/lti/fetchDeepLinkReturn';

import { Lti1p3Form } from '../Lti1p3Launch/Lti1p3Form';
import { lti1p3Start } from '../Lti1p3Launch/lti1p3Start';
import { LtiRequestType } from '../Lti1p3Launch/types';
import {
  ContentContainer,
  ContentHeader,
  ContentHeaderContainer,
  FormErrorMessage,
  HelpLink,
  IFrame,
  LoaderContainer,
  StyledModal,
} from './AssignmentDeepLinkingModal.styled';

type ContentItem = {
  type?: string;
  url?: string;
  title?: string;
  text?: string;
  custom?: Record<string, string>;
  lineItem?: {
    scoreMaximum?: number;
    label?: string;
    resourceId?: string;
    tag?: string;
  };
};

export type AssignmentDeepLinkingModalProps = {
  provider: Lti1P3Provider;
  onSuccess: (assignmentId: string) => void;
  onCancel: () => void;
  onError: (error: Error) => void;
};

const HELP_URL =
  'https://aula.zendesk.com/hc/en-us/articles/360053178513-Using-External-Tools-and-Services-with-Aula';

const daysFromNow = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
};

export const buildAssignmentFromContentItem = ({
  contentItem,
  providerId,
  resourceLinkId,
}: {
  contentItem: ContentItem;
  providerId: string;
  resourceLinkId: string;
}) => {
  const title =
    contentItem.title ||
    contentItem.lineItem?.label ||
    contentItem.text ||
    'LTI assignment';
  const description = contentItem.text || contentItem.title || title;
  const maxGrade =
    typeof contentItem.lineItem?.scoreMaximum === 'number'
      ? contentItem.lineItem.scoreMaximum
      : 100;

  return {
    title,
    description,
    startDate: new Date().toISOString(),
    endDate: daysFromNow(30),
    gradesReleasedAt: daysFromNow(30),
    providerId,
    resourceLinkId,
    maxGrade,
    contentItem,
    ...(contentItem.lineItem?.resourceId
      ? { resourceId: contentItem.lineItem.resourceId }
      : {}),
    ...(contentItem.lineItem?.tag ? { tag: contentItem.lineItem.tag } : {}),
  };
};

/** Split once so JSON payloads may contain `|`. */
export const parseDeepLinkSuccessPayload = (data: string) => {
  const separator = data.indexOf('|');
  if (separator === -1) {
    return undefined;
  }
  const payloadJson = data.slice(separator + 1);
  const parsed = JSON.parse(payloadJson);
  const contentItems = Array.isArray(parsed) ? parsed : parsed?.contentItems;
  if (!Array.isArray(contentItems)) {
    return undefined;
  }
  return {
    contentItems: contentItems as ContentItem[],
    returnToken:
      typeof parsed?.returnToken === 'string' ? parsed.returnToken : undefined,
  };
};

/**
 * Generic Assignments Deep Linking picker — same Aula modal shell as Materials
 * (VitalSource-style), so any admin-configured 1.3 DL tool uses one UX.
 */
export const AssignmentDeepLinkingModal = ({
  provider,
  onSuccess,
  onCancel,
  onError,
}: AssignmentDeepLinkingModalProps) => {
  const iframeId = 'assignment-deep-linking-frame';
  const spinnerSize = 50;
  const centerOffset = `${spinnerSize / 2}px`;

  const user = useSelector(currentUser);
  const spaceId = useSelector(getCurrentSpaceId);
  const { objectId: userId } = user || {};
  const { loginUrl, targetUrl, iss, clientId, description } = provider;

  const [loginHint, setLoginHint] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [hasIframeLoaded, setHasIframeLoaded] = useState(false);
  const resourceLinkIdRef = useRef(uuid());
  const formRef = useRef<HTMLFormElement | null>(null);
  const hasStartedRef = useRef(false);
  const hasHandledReturnRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onCancelRef = useRef(onCancel);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onCancelRef.current = onCancel;
    onErrorRef.current = onError;
  }, [onSuccess, onCancel, onError]);

  useEffect(() => {
    if (hasStartedRef.current || !userId || !clientId || !spaceId) {
      return;
    }
    hasStartedRef.current = true;
    lti1p3Start({
      url: LTI_1P3_START(),
      spaceId,
      clientId,
      type: LtiRequestType.DEEP_LINKING,
      userId,
      resourceLinkId: resourceLinkIdRef.current,
    })
      .then((token) => setLoginHint(token))
      .catch((err) => {
        setError('Failed to start deep linking. Please close and try again.');
        onErrorRef.current(err);
      });
  }, [userId, clientId, spaceId]);

  useEffect(() => {
    if (loginHint) {
      formRef.current?.requestSubmit();
    }
  }, [loginHint]);

  useEffect(() => {
    const apiUrl = API_URL();
    const handleOnMessage = async ({ origin, data }: MessageEvent) => {
      if (origin !== apiUrl) {
        return;
      }

      if (typeof data === 'string' && data.startsWith('LTIDeepLinking:Error')) {
        setError('Deep linking failed. Please try again.');
        return;
      }

      if (
        typeof data !== 'string' ||
        !data.startsWith('LTIDeepLinking:Success')
      ) {
        return;
      }

      if (hasHandledReturnRef.current) {
        return;
      }
      hasHandledReturnRef.current = true;

      try {
        setSaving(true);
        const parsed = parseDeepLinkSuccessPayload(data);
        const contentItem = parsed?.contentItems?.[0];

        if (!contentItem) {
          setError('No content was selected.');
          setSaving(false);
          hasHandledReturnRef.current = false;
          return;
        }

        // Claim orphan token first so a delayed opener redirect cannot double-create.
        if (parsed?.returnToken) {
          try {
            await fetchDeepLinkReturn(parsed.returnToken);
          } catch {
            // Already claimed elsewhere — continue with modal create only if we
            // are the first handler; if consume failed because another path
            // created, save may still be needed for iframe-only returns.
          }
        }

        const assignmentData = buildAssignmentFromContentItem({
          contentItem,
          providerId: provider.id,
          resourceLinkId: resourceLinkIdRef.current,
        });

        const {
          data: { id },
        } = await saveLti1p3Assignment(spaceId, assignmentData);

        onSuccessRef.current(id);
      } catch (err) {
        hasHandledReturnRef.current = false;
        setError('Could not create the assignment from the selected content.');
        onErrorRef.current(err as Error);
      } finally {
        setSaving(false);
      }
    };

    window.addEventListener('message', handleOnMessage);
    return () => window.removeEventListener('message', handleOnMessage);
  }, [provider.id, spaceId]);

  if (!loginUrl || !targetUrl || !iss || !clientId) {
    return null;
  }

  const toolName = description || 'integration';

  return (
    <StyledModal
      open
      id="assignment-deep-linking-modal"
      size="default"
      onClose={() => onCancelRef.current()}
    >
      <ContentContainer>
        <ContentHeaderContainer>
          <ContentHeader>Select content in {toolName}</ContentHeader>
          <IconButton
            style={{ color: 'black' }}
            size={24}
            label="Close"
            icon={icons.Close}
            type="secondary"
            onClick={() => onCancelRef.current()}
            data-testid="close-assignment-deep-link-modal"
          />
        </ContentHeaderContainer>
        <HelpLink>
          <span>For help with this integration visit </span>
          <a
            target="_blank"
            rel="noreferrer"
            title="Open help link in new tab"
            href={HELP_URL}
          >
            <span>Aula&apos;s Help Centre</span>
            <icons.OpenInNew
              label="Open help link in new tab"
              fontSize="inherit"
            />
          </a>
        </HelpLink>
        {error ? <FormErrorMessage role="alert">{error}</FormErrorMessage> : null}
        {(saving || !loginHint || !hasIframeLoaded) && !error && (
          <LoaderContainer centerOffset={centerOffset}>
            <Loader size={spinnerSize} />
          </LoaderContainer>
        )}
        <Lti1p3Form
          ref={formRef}
          loginUrl={loginUrl}
          targetUrl={targetUrl}
          clientId={clientId}
          iss={iss}
          type={LtiRequestType.DEEP_LINKING}
          loginHint={loginHint}
          target={iframeId}
        />
        {!error && (
          <IFrame
            id={iframeId}
            name={iframeId}
            title="Assignment deep linking content selector"
            allow="fullscreen"
            data-testid="assignment-lti-iframe"
            onLoad={() => setHasIframeLoaded(true)}
          />
        )}
      </ContentContainer>
    </StyledModal>
  );
};

export default AssignmentDeepLinkingModal;
